import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, map, merge, of, switchMap } from 'rxjs';
import { ChatHeaderActions } from '../chat-header/chat-header.actions';
import { LiveChatActions } from './live-chat.actions';
import { RecaptchaActions } from '../recaptcha/recaptcha.actions';
import { MessagesActions } from '../messages/messages.actions';
import { TranslateService } from '@ngx-translate/core';
import {
  selectAaEligible,
  selectAASendMessageParams,
  selectChatId,
  selectDepartmentId,
  selectLiveChatState,
  selectOperatorName,
  selectOperatorId,
  selectPlatform,
  selectReceivedFirstOperatorMessage,
  selectUnreadMessageCount,
  selectChatKey,
} from './live-chat.reducer';
import { Store } from '@ngrx/store';
import { WindowStateActions } from '../window/window.actions';
import { OverlayActions } from '../overlay-state/overlay.actions';
import { stripHtml } from '../../../utils/stripHtml';
import { getEndOfChatTextKey } from '../../../utils/endOfChatText';
import {
  selectIsSessionExpired,
  selectSessionIdentifier,
} from '../va-session/va-session.reducer';
import { WindowMessagesActions } from '../window-messages/window-messages.actions';
import { selectWindowStateState } from '../window/window.reducer';
import { VaButtonActions } from '../va-button/va-button.actions';
import { matchSafeSendUrl } from '../../../utils/safeSend';
import { GenesysDxApiService } from '../../services/live-chat/genesys-dx-api.service';
import { UserInfoActions } from '../user-info/user-info.actions';
import {
  selectFirstName,
  selectLastName,
} from '../user-info/user-info.reducer';
import { VaSessionService } from '../../services/va-session.service';
import { isUserSignedIn } from '../../../utils/userProfile';
import { TypingActions } from '../typing/typing.actions';
import { GenesysDxFallbackService } from '../../services/live-chat/genesys-dx-fallback.service';
import {
  selectLanguage,
  selectSettingsState,
} from '../settings/settings.reducers';
import {
  ChatWindowStates,
  CreateChatType,
  LiveChatEndReason,
  LiveChatMethods,
  LiveChatPlatform,
  MessageSubType,
  MessageType,
  OverlayType,
} from '../../types/enum';
import { LiveChatPlatformService } from '../../services/live-chat/live-chat-platform.service';
import { AgentAssistService } from '../../services/agent-assist.service';
import { isTransferBetweenPlatforms } from '../../../utils/transfer-live-chat-platform';
import { appConfig } from '../../../init/getAppConfig';
import { selectMessagesState } from '../messages/messages.reducer';

@Injectable({
  providedIn: 'root',
})
export class LiveChatEffects {
  chimeSound: HTMLAudioElement;
  aaOperatorsIds: string[] = [];
  aaDepartmentIds: any[] = [];

  constructor(
    private actions$: Actions,
    private genesysDxBackendService: GenesysDxApiService,
    private genesysDxFallbackService: GenesysDxFallbackService,
    private liveChatPlatformService: LiveChatPlatformService,
    private agentAssistService: AgentAssistService,
    private translateService: TranslateService,
    private vaSessionService: VaSessionService,
    private store: Store
  ) {
    this.chimeSound = new Audio('assets/sound/new_message_chime.mp3');
  }

  connectToLiveChat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LiveChatActions.escalateToLiveChat),
      switchMap(() => {
        const isProactiveSafesendEnabled = true;
        const isCaptchaCheckEnabled = true;
        if (
          isUserSignedIn() ||
          !isProactiveSafesendEnabled ||
          !isCaptchaCheckEnabled
        ) {
          return of(
            UserInfoActions.getUserInfo({
              nextStep: LiveChatActions.connectToLiveChat,
              onError: RecaptchaActions.checkRecaptcha,
            })
          );
        } else {
          return of(RecaptchaActions.checkRecaptcha());
        }
      })
    )
  );

  createLiveChat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        LiveChatActions.connectToLiveChat,
        LiveChatActions.transferBetweenPlatforms
      ),
      concatLatestFrom(() => [
        this.store.select(selectDepartmentId),
        this.store.select(selectPlatform),
        this.store.select(selectFirstName),
        this.store.select(selectLastName),
        this.store.select(selectLanguage),
        this.store.select(selectSessionIdentifier),
      ]),
      switchMap(
        ([
          ,
          departmentId,
          platform,
          firstName,
          lastName,
          language,
          sessionId,
        ]) => {
          if (!departmentId) {
            return of(LiveChatActions.createLiveChatIframe());
          }
          return this.liveChatPlatformService
            .createLiveChat({
              departmentId,
              platform,
              firstName,
              lastName,
              language,
              sessionId,
            })
            .pipe(
              map((response) => {
                if (
                  this.liveChatPlatformService.isDxPlatform(platform) &&
                  !response?.WebSocketURL &&
                  !response?.Message
                ) {
                  throw new Error('WebSocketURL is  empty');
                }
                return LiveChatActions.getCreateChatResponse({
                  response,
                  createChatType: this.liveChatPlatformService.isDxPlatform(
                    platform
                  )
                    ? CreateChatType.API
                    : CreateChatType.GenesysCX,
                });
              }),
              catchError((e) =>
                of(
                  LiveChatActions.handleConnectionError({ error: e }),
                  LiveChatActions.createLiveChatIframe()
                )
              )
            );
        }
      )
    )
  );

  createLiveChatIframe$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LiveChatActions.createLiveChatIframe),
      concatLatestFrom(() => [
        this.store.select(selectFirstName),
        this.store.select(selectLastName),
        this.store.select(selectLanguage),
      ]),
      switchMap(([, firstName, lastName, language]) =>
        this.genesysDxFallbackService
          .createLiveChatIFrame({
            firstName,
            lastName,
            language,
          })
          .pipe(
            map((response) =>
              LiveChatActions.getCreateChatResponse({
                response: response.result,
                createChatType: CreateChatType.IFRAME,
              })
            ),
            catchError((err) =>
              of(LiveChatActions.handleConnectionError({ error: err }))
            )
          )
      )
    )
  );

  handleCreateChatResponse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LiveChatActions.getCreateChatResponse),
      map((action) => {
        const result = action.response;
        if (result.UnavailableReason && result.Message) {
          return MessagesActions.addSimpleDialogMessage({
            messageText: result.Message,
            messageType: MessageType.VA,
            messageSubType: MessageSubType.LIVE_CHAT_UNAVAILABLE,
          });
        }
        return LiveChatActions.handleCreateChatSuccess({
          webSocketURL: result.WebSocketURL,
          chatKey: result.ChatKey,
          chatId: result.ChatID,
          clientId: result.VisitorID,
          queueApiKey: result.queueApiKey,
        });
      })
    )
  );

  createLiveChatWebSocket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LiveChatActions.handleCreateChatSuccess),
      concatLatestFrom(() => this.store.select(selectPlatform)),
      map(([action, platform]) => {
        this.liveChatPlatformService
          .getPlatformService(platform)
          .createLiveChatWebSocket({
            webSocketURL: action.webSocketURL,
            chatKey: action.chatKey,
            chatId: action.chatId,
            clientId: action.clientId,
          });
        return LiveChatActions.setLiveChatState({
          chatId: action.chatId,
          chatKey: action.chatKey,
          clientId: action.clientId,
          webSocketURL: action.webSocketURL,
          queueApiKey: action.queueApiKey,
        });
      })
    )
  );

  startAgentAssistSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        LiveChatActions.handleCreateChatSuccess,
        LiveChatActions.setChatId
      ),
      concatLatestFrom(() => [
        this.store.select(selectAaEligible),
        this.store.select(selectSessionIdentifier),
        this.store.select(selectChatId),
      ]),
      filter(
        ([, aaEligible, sessionId, chatId]) =>
          aaEligible && chatId && sessionId && isUserSignedIn()
      ),
      switchMap(([, , sessionId, chatId]) =>
        this.agentAssistService.startAgentAssistSession(chatId, sessionId).pipe(
          map(() =>
            LiveChatActions.setAgentAssistSession({
              isAASessionCreated: true,
            })
          ),
          catchError(() =>
            of(
              LiveChatActions.setAgentAssistSession({
                isAASessionCreated: false,
              })
            )
          )
        )
      )
    )
  );

  /*
   * Get AgentAssist Operator Ids on the following conditions:
   * - User is signed in
   * - aaOperatorsIds array is empty
   * */
  getAgentAssistOperatorsIds$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          LiveChatActions.handleCreateChatSuccess,
          WindowMessagesActions.onAppRefresh
        ),
        filter(() => !this.aaOperatorsIds?.length && isUserSignedIn()),
        switchMap(() =>
          this.agentAssistService.getAgentAssistOperatorsIds().pipe(
            map((result) => {
              this.aaOperatorsIds = result;
            })
          )
        )
      ),
    { dispatch: false }
  );

  /*
   * Get AgentAssist Department Ids on the following conditions:
   * - User is signed in
   * - aaDepartmentIds array is empty
   * */
  getAgentAssistDepartmentIds$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          LiveChatActions.handleCreateChatSuccess,
          WindowMessagesActions.onAppRefresh
        ),
        filter(() => !this.aaDepartmentIds?.length && isUserSignedIn()),
        switchMap(() =>
          this.agentAssistService.getAgentAssistDepartmentIds().pipe(
            map((result) => {
              this.aaDepartmentIds = result;
            })
          )
        )
      ),
    { dispatch: false }
  );

  setOperator$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LiveChatActions.receiveOperatorMessage),
      concatLatestFrom(() => this.store.select(selectOperatorId)),
      filter(([action, operatorId]) => action.operatorId !== operatorId),
      switchMap(([action]) => {
        return merge(
          of(LiveChatActions.getDepartmentId()),
          of(
            LiveChatActions.setOperator({
              operatorName: action.operatorName,
              operatorId: action.operatorId,
            })
          )
        );
      })
    )
  );

  // Get department Id
  getDepartmentId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LiveChatActions.getDepartmentId),
      concatLatestFrom(() => this.store.select(selectChatId)),
      switchMap(([, chatId]) =>
        this.agentAssistService.getDepartmentId(chatId).pipe(
          map((result) => {
            return LiveChatActions.setDepartmentId({
              departmentId: result.departmentId,
            });
          })
        )
      )
    )
  );

  /*
   * Send AA Dialog message on the following conditions:
   * - isAASessionCreated
   * - User is signed in
   * - Operator id is in the list
   * */
  sendMessageToAgentAssist$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MessagesActions.handleUserMessage),
        concatLatestFrom(() => this.store.select(selectAASendMessageParams)),
        filter(
          ([, { isAASessionCreated, operatorId, departmentId }]) =>
            isAASessionCreated &&
            isUserSignedIn() &&
            (!!appConfig.apiEnv || //non production environment will always have apiEnv
              this.aaOperatorsIds?.includes(operatorId) ||
              this.aaDepartmentIds?.some((dept) => dept.id == departmentId))
        ),
        switchMap(([action, { chatId, sessionId, departmentId }]) =>
          this.agentAssistService.sendAgentAssistMessage(
            action.query,
            chatId,
            sessionId,
            departmentId
          )
        )
      ),
    { dispatch: false }
  );

  reconnectAfterRefresh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WindowMessagesActions.onAppRefresh),
      concatLatestFrom(() => [
        this.store.select(selectLiveChatState),
        this.store.select(selectSessionIdentifier),
      ]),
      filter(
        ([, liveChatObj]) =>
          (!!liveChatObj.webSocketURL && !!liveChatObj.chatKey) ||
          !!liveChatObj.chatId
      ),
      switchMap(([, liveChatObj, sessionId]) => {
        if (liveChatObj.createChatType === CreateChatType.GenesysCX) {
          return of(
            LiveChatActions.createWebSocketClient({
              webSocketURL: liveChatObj.webSocketURL,
            })
          );
        }
        if (liveChatObj.createChatType === CreateChatType.IFRAME) {
          return this.genesysDxFallbackService
            .createLiveChatIFrame({
              chatKey: liveChatObj.chatKey,
            })
            .pipe(
              map((response) =>
                LiveChatActions.createWebSocketClient({
                  webSocketURL: response.result.WebSocketURL,
                })
              )
            );
        }
        return this.genesysDxBackendService
          .startLiveChatClient({
            platform: liveChatObj.platform,
            chatKey: liveChatObj.chatKey,
            departmentId: liveChatObj.departmentId,
            sessionId,
          })
          .pipe(
            map((response) =>
              LiveChatActions.createWebSocketClient({
                webSocketURL: response.WebSocketURL,
              })
            )
          );
      })
    )
  );

  createNewWebSocketClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LiveChatActions.createWebSocketClient),
      concatLatestFrom(() => [
        this.store.select(selectChatKey),
        this.store.select(selectPlatform),
        this.store.select(selectChatId),
      ]),
      map(([action, chatKey, platform, chatId]) => {
        this.liveChatPlatformService
          .getPlatformService(platform)
          .createLiveChatWebSocket({
            webSocketURL: action.webSocketURL,
            chatKey,
            chatId,
          });
        return LiveChatActions.reconnectSuccessfully();
      })
    )
  );

  updateChatType$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LiveChatActions.setLiveChatState, LiveChatActions.setChatId),
        concatLatestFrom(() => [
          this.store.select(selectSessionIdentifier),
          this.store.select(selectChatId),
        ]),
        filter(([, sessionId, chatId]) => !!sessionId && !!chatId),
        switchMap(([, sessionId, chatId]) =>
          this.vaSessionService.updateChatType(sessionId, chatId)
        )
      ),
    { dispatch: false }
  );

  listenToLiveChatSocketMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        LiveChatActions.setLiveChatState,
        LiveChatActions.reconnectSuccessfully
      ),
      concatLatestFrom(() => this.store.select(selectPlatform)),
      switchMap(
        ([, platform]) =>
          this.liveChatPlatformService.getPlatformService(platform)
            .liveChatResponse$
      ),
      concatLatestFrom(() => [
        this.store.select(selectLiveChatState),
        this.store.select(selectMessagesState),
      ]),
      switchMap(([response, liveChatState, messages]) => {
        switch (response?.method) {
          case LiveChatMethods.setConnection:
            if (!liveChatState.isConnected) {
              return of(LiveChatActions.setConnection());
            }
            break;
          case LiveChatMethods.setChatId:
            return of(LiveChatActions.setChatId({ value: response.value }));
          case LiveChatMethods.closeConnection:
            return of(
              LiveChatActions.endLiveChat({
                reason: LiveChatEndReason.OPERATOR_ENDED,
              })
            );
          case LiveChatMethods.receiveOperatorMessage:
            if (
              !messages?.some(
                (item) => item.messageId == response.value.messageId
              )
            ) {
              if (liveChatState.receivedFirstOperatorMessage) {
                return of(
                  LiveChatActions.receiveOperatorMessage({
                    messageId: response.value.messageId,
                    text: response.value.text,
                    operatorName: response.value.operatorName,
                    operatorId: response.value.operatorId,
                  })
                );
              }
              return of(
                MessagesActions.removeLastNMessages({ numberOfItems: 1 }),
                LiveChatActions.assignedToFirstOperator(),
                LiveChatActions.receiveOperatorMessage({
                  messageId: response.value.messageId,
                  text: response.value.text,
                  operatorName: response.value.operatorName,
                  operatorId: response.value.operatorId,
                })
              );
            }
            break;
          case LiveChatMethods.receiveSystemMessage:
            if (liveChatState.receivedFirstOperatorMessage) {
              return of(
                LiveChatActions.receiveSystemMessage({
                  messageId: response.value.messageId,
                  text: response.value.text,
                })
              );
            }
            return of(
              LiveChatActions.updateLiveChatWaiting({
                text: response.value.text,
                position: null,
                waitTime: null,
              })
            );
          case LiveChatMethods.positionChanged:
            if (!liveChatState.receivedFirstOperatorMessage) {
              return of(
                LiveChatActions.updateLiveChatWaiting({
                  text: '',
                  position: response.value.position,
                  waitTime: response.value.waitTime,
                }),
                LiveChatActions.waitingForAnAgent()
              );
            }
            break;
          case LiveChatMethods.setOperatorTyping:
            return of(TypingActions.setAgentTyping({ value: response.value }));
        }
        return of(LiveChatActions.waitingForMessages());
      })
    )
  );

  addPlainSystemMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LiveChatActions.receiveSystemMessage),
      map((action) =>
        MessagesActions.addPlainSystemMessage({
          messageId: action.messageId,
          text: action.text,
        })
      )
    )
  );

  // Close Connection when user Cancels
  removeConnectionMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LiveChatActions.userCancelConnection),
      switchMap(() =>
        of(
          MessagesActions.removeLastNMessages({ numberOfItems: 1 }),
          LiveChatActions.endLiveChat({
            reason: LiveChatEndReason.USER_CANCELED,
          })
        )
      )
    )
  );

  // Close Connection & close Window
  closeConnection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LiveChatActions.endLiveChat),
      concatLatestFrom(() => [
        this.store.select(selectReceivedFirstOperatorMessage),
        this.store.select(selectIsSessionExpired),
      ]),
      switchMap(([action, isLiveChatActive, isSessionExpired]) => {
        if (
          isSessionExpired ||
          !isLiveChatActive ||
          action.reason == LiveChatEndReason.USER_CANCELED
        ) {
          /*
           * close the window without showing the transcript option
           * */
          return of(WindowStateActions.closeWindow());
        } else {
          return of(
            MessagesActions.addEndOfChatMessage({ reason: action.reason }),
            OverlayActions.showOverlay({ value: OverlayType.Transcript })
          );
        }
      })
    )
  );

  handleCloseLiveChatConnection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        LiveChatActions.endLiveChat,
        LiveChatActions.closeOldConnectionBeforeTransfer
      ),
      concatLatestFrom(() => [
        this.store.select(selectLiveChatState),
        this.store.select(selectSessionIdentifier),
        this.store.select(selectPlatform),
        this.store.select(selectFirstName),
        this.store.select(selectLastName),
      ]),
      switchMap(
        ([action, liveChatState, sessionId, platform, firstName, lastName]) => {
          const reason = getEndOfChatTextKey(action.reason);
          const endChatMessage = reason
            ? this.translateService.instant(reason)
            : '';
          this.liveChatPlatformService
            .getPlatformService(platform)
            .closeConnection({
              endChatMessage,
              firstName,
              lastName,
            });
          if (liveChatState.createChatType === CreateChatType.IFRAME) {
            return this.genesysDxFallbackService
              .finishChat(
                liveChatState.chatKey,
                liveChatState.clientId,
                liveChatState.queueApiKey
              )
              .pipe(
                map(() =>
                  LiveChatActions.onConnectionClosed({ reason: action.reason })
                ),
                catchError(() =>
                  of(
                    LiveChatActions.onConnectionClosed({
                      reason: action.reason,
                    })
                  )
                )
              );
          }
          if (liveChatState.createChatType === CreateChatType.API) {
            return this.genesysDxBackendService
              .finishChat({
                departmentId: liveChatState.departmentId,
                platform: liveChatState.platform,
                chatKey: liveChatState.chatKey,
                clientId: liveChatState.clientId,
                sessionId,
              })
              .pipe(
                map(() =>
                  LiveChatActions.onConnectionClosed({ reason: action.reason })
                ),
                catchError(() =>
                  of(
                    LiveChatActions.onConnectionClosed({
                      reason: action.reason,
                    })
                  )
                )
              );
          }
          return of(
            LiveChatActions.onConnectionClosed({ reason: action.reason })
          );
        }
      )
    )
  );

  checkOperatorMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LiveChatActions.receiveOperatorMessage),
      map((action) => {
        if (isTransferBetweenPlatforms(action.text)) {
          return LiveChatActions.closeOldConnectionBeforeTransfer({
            reason: LiveChatEndReason.PLATFORM_TRANSFER,
          });
        }
        const safeSendUrl = matchSafeSendUrl(action.text);
        if (safeSendUrl) {
          return LiveChatActions.setSafeSendUrl({
            safeSendUrl,
            messageId: action.messageId,
          });
        }
        return LiveChatActions.addOperatorMessage({
          messageId: action.messageId,
          text: action.text,
          operatorName: action.operatorName,
        });
      })
    )
  );

  // Add Operator Message
  addOperatorMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LiveChatActions.addOperatorMessage),
      switchMap((action) => {
        const messageText = stripHtml(action.text);
        const operatorName = action.operatorName;
        return of(
          MessagesActions.addSimpleDialogMessage({
            messageId: action.messageId,
            messageText,
            messageType: MessageType.OPERATOR,
            operatorName,
          })
        );
      })
    )
  );

  notifyUser$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LiveChatActions.receiveOperatorMessage),
        concatLatestFrom(() => this.store.select(selectSettingsState)),
        filter(([, settingsState]) => settingsState.notifications === true),
        map(() => {
          this.chimeSound.play();
        })
      ),
    { dispatch: false }
  );

  updateUnreadCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LiveChatActions.receiveOperatorMessage),
      concatLatestFrom(() => this.store.select(selectWindowStateState)),
      filter(([, windowState]) => windowState === ChatWindowStates.MINIMIZED),
      switchMap(() => {
        return of(LiveChatActions.incrementUnreadCount());
      })
    )
  );

  sendUnreadCountMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LiveChatActions.incrementUnreadCount),
      concatLatestFrom(() => [
        this.store.select(selectOperatorName),
        this.store.select(selectUnreadMessageCount),
      ]),
      switchMap(([, agentName, unreadMessageCount]) => {
        return of(
          VaButtonActions.updateUnreadMessageCount({
            agentName,
            unreadMessageCount,
          })
        );
      })
    )
  );

  // Add Connect to an agent system message
  assignedToFirstOperator$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LiveChatActions.assignedToFirstOperator),
      switchMap(() =>
        of(
          MessagesActions.addPlainSystemMessage({
            text: this.translateService.instant('CHAT.CONNECTED_TO_AGENT'),
          })
        )
      )
    )
  );

  changeLiveChatHeader$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LiveChatActions.waitingForAnAgent),
      concatLatestFrom(() =>
        this.store.select(selectReceivedFirstOperatorMessage)
      ),
      filter(
        ([, receivedFirstOperatorMessage]) => receivedFirstOperatorMessage
      ),
      switchMap(() => {
        // The customer is transferred to another department
        // Header title will be changes to 'Live Chat'
        return of(
          ChatHeaderActions.setTitle({
            value: this.translateService.instant('CHAT.LIVE_CHAT'),
          })
        );
      })
    )
  );

  // Listen to System Message while waiting
  updateLiveChatWaitingMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LiveChatActions.updateLiveChatWaiting),
      map((action) =>
        MessagesActions.updateLiveChatSystemMessage({
          text: action.text,
          queuePosition:
            action.position !== null && action.position > 0
              ? 'Queue position: ' + Number(action.position + 1)
              : '',
          estimatedTime:
            action.waitTime && action.waitTime >= 0
              ? 'Estimate Time to Agent: ' + action.waitTime + ' minutes'
              : '',
        })
      )
    )
  );

  // Transfer Between Platforms
  TransferBetweenPlatforms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LiveChatActions.onConnectionClosed),
      filter((action) => action.reason === LiveChatEndReason.PLATFORM_TRANSFER),
      concatLatestFrom(() => [this.store.select(selectPlatform)]),
      map(([, platform]) => {
        const targetPlatform =
          platform == LiveChatPlatform.dx
            ? LiveChatPlatform.cx
            : LiveChatPlatform.dx;
        return LiveChatActions.transferBetweenPlatforms({
          targetPlatform,
        });
      })
    )
  );
}
